// Session inspector — the editor for one day's activity cell: slot override,
// coach note, CAN'T DO THIS (re-plan the coming days around a change),
// operator notes, .fit result / upload (run + padel), run prescription,
// strength exercise table (done boxes, loads, per-set targets, Speediance
// actuals + SYNC) and the done / push footer.
//
// Reads through `useDayData(date)` so every write is the same optimistic
// mutation the ledger grid already observes. The .fit summary, coach note and
// run target come from the week ledger (same query cache).

import { useEffect, useRef, useState } from 'react';
import { formatApiError } from '../../lib/api';
import { mondayOf } from '../../lib/cardio-queries';
import { type AdjustChange, useRequestAdjustment } from '../../lib/coach-adjust';
import { C } from '../../lib/design';
import {
  formatRunTarget,
  isoToday,
  useCoachProgramWeek,
  useDayData,
  useSpeedianceMapping,
} from '../../lib/day-data';
import {
  PICKABLE_SESSION_TYPES,
  dayLabel,
  fmt1,
  fmtClock,
  isPadelType,
  isRestType,
  isRunType,
  sessionLabel,
  sessionShortLabel,
} from '../../lib/ledger';
import { useLedgerWeek } from '../../lib/ledger-week';
import { RUN_PLAN, hrZones } from '../../lib/protocol';
import { buildCoachSessionComparisons } from '../../lib/speediance-feedback';
import { resolveStrengthSession } from '../../lib/strength-session';
import type { SyncResponse } from '../../lib/types';
import { CoachPushSheet } from '../CoachPushSheet';
import { CoachSessionComparison } from '../CoachSessionComparison';
import { BodyweightActualEditor } from '../day/BodyweightActualEditor';
import { AdjustmentOutcome } from '../AdjustmentProposal';
import { SurfaceComments } from '../day/SurfaceComments';
import { FitDrop, PadelResult, RunResult } from './cardio';
import { useFitUpload, useSpeedianceSync } from './hooks';
import { InspHead } from './InspHead';
import { Btn, ErrorStrip, Field, INPUT_STYLE, Lbl, Note, Seg, Sub } from './ui';

const ROW_GRID = { display: 'grid', gridTemplateColumns: '18px 1fr 60px 64px', gap: 8 } as const;
const DASHED = { border: `1px dashed ${C.line}`, padding: '10px 12px' } as const;
const LOAD_SAVE_DEBOUNCE_MS = 400;
// Stable identity: useLedgerWeek memoizes on this argument.
const NO_MEAL_ITEM_DATES: readonly string[] = [];

/** Numeric load input that saves as you type (debounced) and on blur. */
function LoadInput({ value, onSave, label }: { value: number | undefined; onSave: (kg: number) => void; label: string }) {
  const [text, setText] = useState(value != null ? String(value) : '');
  const [focused, setFocused] = useState(false);
  const timer = useRef<number | null>(null);
  const pending = useRef<number | null>(null);

  // Follow the cache while the operator isn't typing (optimistic update,
  // sync-driven bump) without clobbering an in-progress edit.
  useEffect(() => {
    if (!focused) setText(value != null ? String(value) : '');
  }, [value, focused]);

  // Latest save callback in a ref so the debounce timer and the unmount
  // flush never call a stale closure (the inspector re-renders on every
  // optimistic cache update).
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const flush = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    if (pending.current != null) {
      onSaveRef.current(pending.current);
      pending.current = null;
    }
  };
  // Switching cells mid-edit must not drop the typed load. `flush` only
  // touches refs, so the first render's closure is safe here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => flush, []);

  return (
    <input
      className="num"
      type="number"
      step={2.5}
      min={0}
      inputMode="decimal"
      aria-label={label}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        flush();
      }}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const kg = Number(next);
        if (next === '' || !Number.isFinite(kg) || kg < 0 || kg === value) return;
        pending.current = kg;
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(flush, LOAD_SAVE_DEBOUNCE_MS);
      }}
      style={{ ...INPUT_STYLE, padding: '4px 6px', fontSize: 12, textAlign: 'right' }}
    />
  );
}

/** `SYNCED 12 SETS · FRI 18 SEP`, then the progression engine's verdict per
 *  exercise (named through the prescription so `g:42` reads as the
 *  exercise) and any read-back warnings. */
function SyncOutcome({ result, names }: { result: SyncResponse; names: Record<string, string> }) {
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* `range.until` may be a datetime; the day is what matters here. */}
      <Sub color={C.signal}>
        SYNCED {result.synced} SET{result.synced === 1 ? '' : 'S'} · {dayLabel(result.range.until.slice(0, 10))}
      </Sub>
      {result.engine_verdicts.map((v, i) => {
        const name = names[v.exercise_key] ?? v.exercise_key;
        switch (v.kind) {
          case 'bump':
            return (
              <Sub key={i} color={C.signal}>
                ↑ {name} {fmt1(v.prev_target)} → {fmt1(v.new_target)} kg · {v.reps_at_target}/{v.total_reps} reps
              </Sub>
            );
          case 'hold':
            return (
              <Sub key={i}>
                → {name} held at {fmt1(v.target)} kg · {v.reps_at_target}/{v.total_reps} reps
              </Sub>
            );
          case 'skipped':
            return (
              <Sub key={i} color={C.faint}>
                · {name} skipped · {v.reason.replaceAll('_', ' ')}
              </Sub>
            );
        }
      })}
      {result.warnings.map((w, i) => (
        <Sub key={`w${i}`} color={C.warn}>
          ! {w.detail ?? w.kind}
        </Sub>
      ))}
    </div>
  );
}

const CHANGE_OPTIONS: ReadonlyArray<[AdjustChange, string]> = [
  ['no_speediance', 'NO SPEEDIANCE'],
  ['short_on_time', 'SHORT ON TIME'],
  ['unavailable', "CAN'T TRAIN"],
];

/** Re-plan the coming days around a change (no Speediance, short on time,
 *  can't train) starting on `date`, without a full coach rebuild. The
 *  proposal renders inline so the decision happens where the problem was
 *  noticed; the coach digest also picks it up after a reload. */
function CantDoThis({ date }: { date: string }) {
  const [open, setOpen] = useState(false);
  // No default: picking the wrong change would spend a ~30 s model call.
  const [change, setChange] = useState<AdjustChange | undefined>(undefined);
  const [through, setThrough] = useState(date);
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');
  const adjust = useRequestAdjustment();

  if (!open) {
    return (
      <div style={{ marginTop: 14 }}>
        <Btn ghost onClick={() => setOpen(true)}>
          CAN'T DO THIS
        </Btn>
      </div>
    );
  }

  const shortOnTime = change === 'short_on_time';
  const valid =
    change !== undefined &&
    (shortOnTime
      ? Number.isInteger(minutes) && minutes >= 10 && minutes <= 240
      : /^\d{4}-\d{2}-\d{2}$/.test(through) && through >= date);
  const trimmedNote = note.trim();

  return (
    <div style={{ ...DASHED, marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Lbl>CAN'T DO THIS</Lbl>
      <Seg<AdjustChange>
        wrap
        ariaLabel="What changed"
        options={CHANGE_OPTIONS}
        value={change}
        onChange={setChange}
      />
      {change !== undefined &&
        (shortOnTime ? (
          <Field label="MINUTES AVAILABLE" value={minutes} onChange={setMinutes} min={10} suffix="min" width={90} />
        ) : (
          <label style={{ display: 'block' }}>
            <Lbl style={{ marginBottom: 4 }}>THROUGH</Lbl>
            <input
              type="date"
              className="num"
              aria-label="Through"
              min={date}
              value={through}
              onChange={(e) => setThrough(e.target.value)}
              style={{ ...INPUT_STYLE, width: 170 }}
            />
          </label>
        ))}
      <label style={{ display: 'block' }}>
        <Lbl style={{ marginBottom: 4 }}>NOTE · OPTIONAL</Lbl>
        <input
          type="text"
          aria-label="Note"
          maxLength={200}
          placeholder="e.g. construction work at home"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={INPUT_STYLE}
        />
      </label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Btn
          primary
          disabled={!valid || adjust.isPending}
          onClick={() => {
            if (change === undefined) return;
            adjust.mutate({
              start_date: date,
              // SHORT ON TIME has no range input: it is about this one day.
              end_date: shortOnTime ? date : through,
              change,
              ...(shortOnTime ? { minutes } : {}),
              ...(trimmedNote ? { note: trimmedNote } : {}),
            });
          }}
        >
          {adjust.isPending ? 'RE-PLANNING YOUR WEEK… (~30 s)' : 'RE-PLAN'}
        </Btn>
        {!adjust.isPending && (
          <Btn
            onClick={() => {
              adjust.reset();
              setOpen(false);
            }}
          >
            CANCEL
          </Btn>
        )}
      </div>
      <AdjustmentOutcome data={adjust.data} error={adjust.error} />
    </div>
  );
}

export function SessionInspector({ date }: { date: string }) {
  const today = isoToday();
  const isToday = date === today;
  const isPast = date < today;
  const isFuture = date > today;

  const day = useDayData(date);
  const mappingRows = useSpeedianceMapping();
  const week = useLedgerWeek(mondayOf(date), NO_MEAL_ITEM_DATES);
  const ledgerDay = week.byDate.get(date);
  const programWeek = useCoachProgramWeek(today).week;

  const plan = day.todayPlan;
  const workout = day.todayWorkout;
  const type = plan?.type;
  const presc = workout?.coach_prescription ?? null;
  const coachSession = workout?.coach_strength_session ?? null;
  const resolved = resolveStrengthSession({ coachSession, coachTitle: presc?.name, mappingRows });
  const isRun = isRunType(type);
  const isPadel = isPadelType(type);
  const rest = isRestType(type);
  const done = day.todayDone;
  const fit = ledgerDay?.session.fit ?? null;
  const runTarget = ledgerDay?.session.runTarget ?? null;
  const coachNote = ledgerDay?.coachNote ?? presc?.reason?.trim() ?? null;

  const fitUpload = useFitUpload(isPadel ? 'padel' : 'run');
  const fitErrors = fitUpload.results.filter((r) => r.status === 'failed');
  const [pushOpen, setPushOpen] = useState(false);

  const workoutMut = day.mutations.workout;
  const overrideMut = day.mutations.workoutOverride;
  const loadMut = day.mutations.exerciseWeight;
  const bodyweightMut = day.mutations.bodyweightActual;
  const sync = useSpeedianceSync();

  // Auto-complete: once every exercise of today's strength session is
  // accounted for — synced actuals under its actualsKey, OR ticked by hand
  // (bodyweight / unmapped moves like a plank never get Speediance actuals) —
  // flip the session to done (status + all boxes) exactly once per date, so a
  // later explicit un-mark isn't fought by the effect re-firing. Gated to
  // today — viewing a past day must never write.
  const exerciseKeys = resolved?.exercises.map((ex) => ({ key: ex.key, actualsKey: ex.actualsKey })) ?? [];
  const allExercisesLogged =
    exerciseKeys.length > 0 &&
    exerciseKeys.every(
      (e) => Object.hasOwn(day.sessionTimeTargetMap, e.actualsKey) || day.exercisesDone[e.key] === true,
    );
  const autoCompletedDates = useRef<Set<string>>(new Set());
  const workoutMutate = workoutMut.mutate;
  useEffect(() => {
    if (!isToday || !allExercisesLogged || done || !type) return;
    if (autoCompletedDates.current.has(date)) return;
    autoCompletedDates.current.add(date);
    workoutMutate({
      date,
      type,
      status: 'completed',
      exercises_done: Object.fromEntries(exerciseKeys.map((e) => [e.key, true])),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, allExercisesLogged, done, type, date]);

  // No workout_log row and no weekday commitment — the day is genuinely
  // unplanned, which is the normal state now that the static rotation is
  // gone. Still offer the SLOT picker: claiming the day writes the row.
  if (!plan || !type) {
    return (
      <div>
        <InspHead date={date} title="—" right={<Sub color={C.dim}>UNPLANNED</Sub>} />
        <div style={{ padding: 16 }}>
          <Lbl style={{ marginBottom: 6 }}>SLOT</Lbl>
          <Sub style={{ marginBottom: 8 }}>
            {day.queries.workouts.isLoading
              ? 'Loading…'
              : 'Nothing planned for this day. Pick a slot to claim it.'}
          </Sub>
          <div
            style={{
              opacity: overrideMut.isPending ? 0.5 : 1,
              pointerEvents: overrideMut.isPending ? 'none' : undefined,
            }}
          >
            <Seg<string>
              wrap
              ariaLabel="Session slot"
              options={PICKABLE_SESSION_TYPES.map(([t]) => [t, sessionShortLabel(t)])}
              value={undefined}
              onChange={(t) => overrideMut.mutate({ date, type: t })}
            />
          </div>
          {overrideMut.error && (
            <div style={{ marginTop: 8 }}>
              <ErrorStrip message={formatApiError(overrideMut.error)} />
            </div>
          )}
          {!isPast && <CantDoThis key={date} date={date} />}
        </div>
      </div>
    );
  }

  const status = workout?.status;
  const statusLabel: [string, string] = done
    ? ['✓ DONE', C.signal]
    : status === 'skipped'
      ? ['SKIPPED', C.dim]
      : rest
        ? ['REST', C.faint]
        : isPast
          ? ['NOT LOGGED', C.warn]
          : ['PLANNED', C.dim];

  // Marking the whole session done checks every exercise; un-marking clears
  // them, so the headline and the boxes never disagree.
  const toggleDone = () => {
    const willBeDone = !done;
    workoutMut.mutate({
      date,
      type,
      status: willBeDone ? 'completed' : 'not_yet',
      ...(exerciseKeys.length > 0
        ? { exercises_done: Object.fromEntries(exerciseKeys.map((e) => [e.key, willBeDone])) }
        : {}),
    });
  };

  // Prescription line: the coach's dated target, else the static RUN_PLAN
  // baseline for the program week (what the old week strip showed).
  const baseline = RUN_PLAN[Math.min(programWeek - 1, RUN_PLAN.length - 1)];
  const prescription = runTarget
    ? formatRunTarget(runTarget)
    : isRun
      ? (type === 'Run-Long' ? baseline?.long : baseline?.easy) ?? null
      : null;
  const prescribedZone: string | undefined = runTarget?.zone ?? prescription?.match(/Z[1-5]/)?.[0];
  const zoneRange = hrZones(day.profile.age ?? 45).find((z) => z.name === prescribedZone)?.range;

  const coachComparisons = workout
    ? buildCoachSessionComparisons([workout], day.actualSetsForDate, today).filter(
        (s) => s.date === date && s.hasAnyActuals,
      )
    : [];

  return (
    <div>
      <InspHead
        date={date}
        title={sessionLabel(type, workout?.source === 'coach' ? presc?.name : undefined)}
        right={<Sub color={statusLabel[1]}>{statusLabel[0]}</Sub>}
      />
      <div style={{ padding: 16 }}>
        <Lbl style={{ marginBottom: 6 }}>SLOT</Lbl>
        {workout?.source === 'coach' && (
          <Sub color={C.signal} style={{ marginBottom: 6 }}>
            ◆ COACH · {presc?.name ?? sessionLabel(type)}
          </Sub>
        )}
        <div style={{ opacity: overrideMut.isPending ? 0.5 : 1, pointerEvents: overrideMut.isPending ? 'none' : undefined }}>
          <Seg<string>
            wrap
            ariaLabel="Session slot"
            options={PICKABLE_SESSION_TYPES.map(([t]) => [t, sessionShortLabel(t)])}
            value={type}
            onChange={(t) => {
              if (t !== type) overrideMut.mutate({ date, type: t });
            }}
          />
        </div>
        {overrideMut.error && (
          <div style={{ marginTop: 8 }}>
            <ErrorStrip message={formatApiError(overrideMut.error)} />
          </div>
        )}

        {coachNote && (
          <Note label="◆ COACH" style={{ marginTop: 14 }}>
            {coachNote}
          </Note>
        )}

        {!isPast && <CantDoThis key={date} date={date} />}

        <div style={{ ...DASHED, marginTop: 14 }}>
          <SurfaceComments date={date} surface="activity" isToday={isToday} />
        </div>

        {(isRun || isPadel) && !isFuture && (
          <div style={{ marginTop: 16 }}>
            {fit ? (
              <>
                {isRun ? <RunResult session={fit} /> : <PadelResult session={fit} />}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 12,
                  }}
                >
                  <Sub color={C.faint} className="num">
                    recorded {fmtClock(fit.recorded_at)}
                  </Sub>
                  <Btn onClick={fitUpload.open} disabled={fitUpload.busy}>
                    {fitUpload.busy ? 'UPLOADING…' : 'REPLACE .FIT'}
                  </Btn>
                  <input {...fitUpload.inputProps} />
                </div>
                {fitErrors.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <ErrorStrip
                      message={fitErrors.map((r) => `${r.filename}: ${r.error ?? 'failed'}`).join(' · ')}
                    />
                  </div>
                )}
              </>
            ) : (
              <FitDrop kind={isRun ? 'run' : 'padel'} />
            )}
          </div>
        )}

        {isRun && prescription && (isFuture || runTarget || !fit) && (
          <div style={{ ...DASHED, marginTop: 12 }}>
            <Lbl style={{ marginBottom: 4 }}>PRESCRIPTION{runTarget ? ' · COACH' : ' · BASELINE'}</Lbl>
            <div className="num" style={{ fontSize: 12 }}>
              {prescription}
            </div>
            {zoneRange && (
              <Sub className="num" style={{ marginTop: 4 }}>
                {prescribedZone} ·{' '}
                <span style={{ color: C.signal }}>
                  {zoneRange[0]}–{zoneRange[1]} bpm
                </span>
              </Sub>
            )}
          </div>
        )}
        {type === 'Strength' && !resolved && (
          <div style={{ ...DASHED, marginTop: 12 }}>
            <Lbl style={{ marginBottom: 4 }}>NO PRESCRIPTION</Lbl>
            <Sub>The coach's exercise list appears here once a strength session is applied for this day. Speediance actuals still sync.</Sub>
          </div>
        )}

        {resolved && (
          <div style={{ marginTop: 16 }}>
            <div style={{ ...ROW_GRID, padding: '0 0 6px', borderBottom: `1px solid ${C.line}` }}>
              <span />
              <Lbl>EXERCISE</Lbl>
              <Lbl>SETS</Lbl>
              <Lbl style={{ textAlign: 'right' }}>LOAD KG</Lbl>
            </div>
            {resolved.exercises.map((ex) => {
              // Key presence in sessionTimeTargetMap = synced actuals exist for
              // this exercise on this date → the load is locked to what the
              // operator actually worked against.
              const locked = Object.hasOwn(day.sessionTimeTargetMap, ex.actualsKey);
              const workingKg = day.exerciseWeightsMap[ex.actualsKey] ?? ex.prescribedTopWeightKg;
              const displayKg = locked
                ? (day.sessionTimeTargetMap[ex.actualsKey] ?? workingKg)
                : workingKg;
              // Explicit flag wins; a synced exercise reads done automatically.
              const exDone = day.exercisesDone[ex.key] ?? locked;
              const deload = !locked && (day.deloadFlagMap[ex.actualsKey] ?? false);
              // Uniform sets already read from the load column; only a ramp
              // (warmups → working) adds information.
              const setWeights = ex.sets.map((s) => s.weightKg);
              const ramp = setWeights.some((w) => w !== setWeights[0]) ? setWeights : null;
              // The coach's prescribed top set, shown faint when the load on
              // display (working weight, or the locked session-time target)
              // has moved away from it.
              const rx = ex.prescribedTopWeightKg !== displayKg ? ex.prescribedTopWeightKg : null;
              return (
                <div
                  key={ex.key}
                  style={{ ...ROW_GRID, alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.line}` }}
                >
                  <button
                    type="button"
                    aria-pressed={exDone}
                    aria-label={exDone ? `Mark ${ex.name} not done` : `Mark ${ex.name} done`}
                    disabled={workoutMut.isPending}
                    onClick={() =>
                      workoutMut.mutate({
                        date,
                        type,
                        exercises_done: { ...day.exercisesDone, [ex.key]: !exDone },
                      })
                    }
                    style={{
                      width: 18,
                      height: 18,
                      border: `1px solid ${exDone ? C.signal : C.line}`,
                      background: exDone ? C.signal : 'transparent',
                      color: C.bg,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    {exDone ? '✓' : ''}
                  </button>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: exDone ? C.dim : C.text,
                        textDecoration: exDone ? 'line-through' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ex.name}
                      {ex.isCompound && (
                        <span style={{ color: C.faint, fontSize: 9, letterSpacing: '0.1em', marginLeft: 8 }}>
                          COMPOUND
                        </span>
                      )}
                      {deload && (
                        <span style={{ color: C.warn, fontSize: 9, letterSpacing: '0.1em', marginLeft: 8 }}>
                          DELOAD?
                        </span>
                      )}
                    </div>
                    {ramp && (
                      <Sub className="num" color={C.faint} style={{ marginTop: 2, whiteSpace: 'nowrap' }}>
                        {ramp.join(' · ')}
                      </Sub>
                    )}
                  </div>
                  <Sub className="num" style={{ whiteSpace: 'nowrap' }}>
                    {ex.setsLabel}
                  </Sub>
                  <div style={{ textAlign: 'right' }}>
                    {locked ? (
                      <div className="num" style={{ fontSize: 12, color: C.dim }}>
                        {displayKg}
                      </div>
                    ) : (
                      <LoadInput
                        value={displayKg}
                        label={`${ex.name} load kg`}
                        onSave={(kg) => loadMut.mutate({ exercise_key: ex.actualsKey, weight_kg: kg })}
                      />
                    )}
                    {locked && <Sub color={C.faint}>LOCKED</Sub>}
                    {rx !== null && (
                      <Sub className="num" color={C.faint}>
                        RX {rx}
                      </Sub>
                    )}
                  </div>
                </div>
              );
            })}
            {(workoutMut.error || loadMut.error) && (
              <div style={{ marginTop: 8 }}>
                <ErrorStrip message={formatApiError(workoutMut.error ?? loadMut.error)} />
              </div>
            )}
            <Sub style={{ marginTop: 8 }}>All reps clean → +2.5 kg next time. Loads save as you type.</Sub>
            {coachSession && (
              <BodyweightActualEditor
                session={coachSession}
                actuals={day.actualSetsForDate}
                saving={bodyweightMut.isPending}
                error={bodyweightMut.error}
                onSave={async (exercises) => {
                  await bodyweightMut.mutateAsync({ date, exercises });
                }}
              />
            )}
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <Lbl>ACTUALS</Lbl>
                <Btn onClick={() => sync.mutate(date)} disabled={sync.isPending}>
                  {sync.isPending ? 'SYNCING…' : '↓ SYNC SPEEDIANCE'}
                </Btn>
              </div>
              {sync.data && (
                <SyncOutcome
                  result={sync.data}
                  names={Object.fromEntries(resolved.exercises.map((ex) => [ex.actualsKey, ex.name]))}
                />
              )}
              {sync.error && (
                <div style={{ marginTop: 8 }}>
                  <ErrorStrip message={formatApiError(sync.error)} />
                </div>
              )}
              {coachComparisons.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <CoachSessionComparison sessions={coachComparisons} limit={1} />
                </div>
              )}
            </div>
          </div>
        )}

        {!rest && (
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <Btn primary={!done} onClick={toggleDone} disabled={workoutMut.isPending} style={{ flex: 1 }}>
              {done ? 'UNMARK DONE' : isPast ? 'BACKFILL AS DONE' : 'MARK DONE'}
            </Btn>
            {resolved && !done && <Btn onClick={() => setPushOpen(true)}>PUSH TO SPEEDIANCE</Btn>}
          </div>
        )}
        {!resolved && workoutMut.error && (
          <div style={{ marginTop: 8 }}>
            <ErrorStrip message={formatApiError(workoutMut.error)} />
          </div>
        )}
      </div>

      {resolved && coachSession && (
        <CoachPushSheet
          open={pushOpen}
          onClose={() => setPushOpen(false)}
          session={coachSession}
          title={resolved.title}
        />
      )}
    </div>
  );
}
